import {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  ModalDialog, Spinner, Button,
} from '@openedx/paragon';
import { RiDownloadLine } from '@remixicon/react';
import { renderAsync as renderDocx } from 'docx-preview';
import * as XLSX from 'xlsx';
import { useIntl } from '@edx/frontend-platform/i18n';

import messages from '../messages';

const PREVIEWABLE = ['pdf', 'docx', 'xlsx', 'csv', 'txt', 'png', 'jpg', 'jpeg', 'gif', 'webp'];

const FilePreviewModal = ({ file, isOpen, onClose, onDownload }) => {
  const intl = useIntl();
  const docxContainerRef = useRef(null);
  const xlsxContainerRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [textContent, setTextContent] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const ext = (file?.type || '').toLowerCase();

  useEffect(() => {
    if (!isOpen || !file?.url) { return undefined; }
    let cancelled = false;
    setStatus('loading');
    setErrorMessage('');
    setTextContent('');

    const fetchBlob = async () => {
      const res = await fetch(file.url);
      if (!res.ok) { throw new Error(`Failed to load file (${res.status})`); }
      return res.blob();
    };

    const run = async () => {
      try {
        if (ext === 'docx') {
          const blob = await fetchBlob();
          if (cancelled || !docxContainerRef.current) { return; }
          docxContainerRef.current.innerHTML = '';
          await renderDocx(blob, docxContainerRef.current, undefined, {
            className: 'docx-preview',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
          });
        } else if (ext === 'xlsx' || ext === 'csv') {
          const blob = await fetchBlob();
          const buffer = await blob.arrayBuffer();
          if (cancelled || !xlsxContainerRef.current) { return; }
          const workbook = XLSX.read(buffer, { type: 'array' });
          const sheetsHtml = workbook.SheetNames.map((name) => {
            const sheet = workbook.Sheets[name];
            const html = XLSX.utils.sheet_to_html(sheet, { id: `sheet-${name}` });
            return `<section class="xlsx-sheet"><h4 class="xlsx-sheet__title">${name}</h4>${html}</section>`;
          }).join('');
          xlsxContainerRef.current.innerHTML = sheetsHtml;
        } else if (ext === 'txt') {
          const blob = await fetchBlob();
          const text = await blob.text();
          if (cancelled) { return; }
          setTextContent(text);
        }
        if (!cancelled) { setStatus('ready'); }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(err?.message || intl.formatMessage(messages.previewRenderError));
          setStatus('error');
        }
      }
    };

    if (['docx', 'xlsx', 'csv', 'txt'].includes(ext)) {
      run();
    } else {
      setStatus('ready');
    }

    return () => { cancelled = true; };
  }, [isOpen, file?.url, ext]);

  const renderBody = () => {
    if (!file?.url) { return null; }

    const showError = status === 'error' || !PREVIEWABLE.includes(ext);
    const showLoading = status === 'loading';

    if (showError) {
      return (
        <div className="file-preview-modal__state">
          <p className="file-preview-modal__state-title">
            {intl.formatMessage(messages.previewNotAvailable)}
          </p>
          <p className="file-preview-modal__state-text">
            {ext === 'pptx' || ext === 'ppt'
              ? intl.formatMessage(messages.previewPowerpointHint)
              : errorMessage || intl.formatMessage(messages.previewUnsupported)}
          </p>
          {onDownload && (
            <Button variant="primary" iconBefore={RiDownloadLine} onClick={() => onDownload(file)}>
              {intl.formatMessage(messages.previewDownload)}
            </Button>
          )}
        </div>
      );
    }

    if (ext === 'pdf') {
      return (
        <iframe
          title={file.name || intl.formatMessage(messages.previewPdfTitle)}
          src={file.url}
          className="file-preview-modal__iframe"
        />
      );
    }

    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return (
        <div className="file-preview-modal__image-wrap">
          <img
            src={file.url}
            alt={file.name || intl.formatMessage(messages.previewImageAlt)}
            className="file-preview-modal__image"
          />
        </div>
      );
    }

    if (ext === 'txt') {
      return showLoading ? (
        <div className="file-preview-modal__state">
          <Spinner
            animation="border"
            variant="primary"
            screenReaderText={intl.formatMessage(messages.previewLoading)}
          />
        </div>
      ) : (
        <pre className="file-preview-modal__text">{textContent}</pre>
      );
    }

    return (
      <div className="file-preview-modal__render">
        <div
          ref={docxContainerRef}
          className="file-preview-modal__docx"
          style={{ display: ext === 'docx' && !showLoading ? 'block' : 'none' }}
        />
        <div
          ref={xlsxContainerRef}
          className="file-preview-modal__xlsx"
          style={{ display: (ext === 'xlsx' || ext === 'csv') && !showLoading ? 'block' : 'none' }}
        />
        {showLoading && (
          <div className="file-preview-modal__state">
            <Spinner
              animation="border"
              variant="primary"
              screenReaderText={intl.formatMessage(messages.previewLoading)}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <ModalDialog
      title={file?.name || intl.formatMessage(messages.previewFallbackTitle)}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      hasCloseButton
      isFullscreenOnMobile
      isFullscreenScroll
    >
      <ModalDialog.Header>
        <ModalDialog.Title>
          {file?.name || intl.formatMessage(messages.previewFallbackTitle)}
        </ModalDialog.Title>
      </ModalDialog.Header>
      <ModalDialog.Body className="file-preview-modal__body">
        {renderBody()}
      </ModalDialog.Body>
    </ModalDialog>
  );
};

FilePreviewModal.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
    type: PropTypes.string,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
};

FilePreviewModal.defaultProps = {
  file: null,
  onDownload: undefined,
};

export default FilePreviewModal;
